import React, { memo } from "react";
import Reanimated, {
  ReduceMotion,
  SlideOutLeft,
} from "react-native-reanimated";

import InfoRecommendation from "@/features/recommendations/components/organisms/InfoRecommendation";

import AppleStyleSwipeableRow from "@/components/molecules/AppleStyleSwipeableRow";
import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import { RecommendationsPlayerFragment } from "@/generated/graphql";
/**
 * Props interface for RecommendationPlayer view component
 */
interface Props {
  player: RecommendationsPlayerFragment;
  showActionsheet?: boolean;
  isPlayerInAnyWatchlist?: boolean;
  isDeleting?: boolean;
  rankChange: number;
  playerName: string;
  positionAndTeamName: string;
  promptRef?: React.RefObject<CustomPromptRefProps>;
  isSmallScreen: boolean;
  onAdd: () => void;
  onAddWatchlistPress: () => void;
  onOpenPlayerDetail: () => void;
  onCloseActionSheet: () => void;
  onPressPlayer: () => void;
  onSwipeableOpen?: (direction: "left" | "right") => void;
  onRemovePlayer: () => void;
  isDark: boolean;
}

/**
 * View component for swipeable recommendation player item
 * @remarks
 * Wraps InfoRecommendation in a swipeable container with delete action
 * @param root0 - Component props
 * @param root0.player - Player watchlist fragment with basic player data
 * @param root0.promptRef - Ref to custom prompt component for confirmation dialogs
 * @param root0.playerName - Formatted player name for display
 * @param root0.rankChange - Player's rank change value (positive/negative/zero)
 * @param root0.isDeleting - Whether player is being removed from recommendations
 * @param root0.showActionsheet - Whether add-to-watchlist action sheet is visible
 * @param root0.positionAndTeamName - Formatted position and team name string
 * @param root0.isPlayerInAnyWatchlist - Whether player exists in any watchlist
 * @param root0.isSmallScreen - Whether screen size is mobile/small
 * @param root0.onAdd - Callback fired when add button is pressed
 * @param root0.onAddWatchlistPress - Callback fired when adding player to watchlist
 * @param root0.onCloseActionSheet - Callback fired when closing action sheet
 * @param root0.onSwipeableOpen - Callback fired when swipeable row opens
 * @param root0.onPressPlayer - Callback fired when player card is pressed
 * @param root0.onOpenPlayerDetail - Callback fired to navigate to player detail
 * @param root0.onRemovePlayer - Callback fired when removing player from recommendations
 * @param root0.isDark - Whether dark mode is active
 */
const RecommendationPlayerView = ({
  player,
  promptRef,
  playerName,
  rankChange,
  isDeleting,
  showActionsheet,
  positionAndTeamName,
  isPlayerInAnyWatchlist,
  isSmallScreen,
  onAdd,
  onAddWatchlistPress,
  onCloseActionSheet,
  onSwipeableOpen,
  onPressPlayer,
  onOpenPlayerDetail,
  onRemovePlayer,
  isDark,
}: Props) =>
  !isSmallScreen ? (
    <InfoRecommendation
      rankChange={rankChange}
      playerName={playerName}
      isDeleting={isDeleting}
      positionAndTeamName={positionAndTeamName}
      onOpenPlayerDetail={onOpenPlayerDetail}
      onPressPlayer={onPressPlayer}
      isPlayerInAnyWatchList={!!isPlayerInAnyWatchlist}
      onAddPress={onAdd}
      player={player}
      promptRef={promptRef}
      showActionsheet={showActionsheet}
      isPlayerInAnyWatchlist={!!isPlayerInAnyWatchlist}
      isSmallScreen={isSmallScreen}
      onCloseActionSheet={onCloseActionSheet}
      onAddWatchlistPress={onAddWatchlistPress}
      onRemovePlayer={onRemovePlayer}
      isDark={isDark}
    />
  ) : (
    <Reanimated.View exiting={SlideOutLeft.reduceMotion(ReduceMotion.System)}>
      {/* SE-5434: use the wrapper's standard delete action (labelled "Drop"
       * circle, confirm dialog via onRemovePlayer) instead of the previous
       * custom raw red block with an X — reported as a rendering artifact
       * obscuring the card, and it deleted without confirmation. Dropping
       * the custom renderer also drops overshootRight, so the action can
       * no longer overshoot across the card. */}
      <AppleStyleSwipeableRow
        isPlayerInAnyWatchlist={isPlayerInAnyWatchlist}
        onAdd={isDeleting ? undefined : onAdd}
        onDelete={isDeleting ? undefined : onRemovePlayer}
        rightThreshold={40}
        onSwipeableOpen={onSwipeableOpen}
      >
        <InfoRecommendation
          rankChange={rankChange}
          playerName={playerName}
          isDeleting={isDeleting}
          positionAndTeamName={positionAndTeamName}
          onOpenPlayerDetail={onOpenPlayerDetail}
          onPressPlayer={onPressPlayer}
          isPlayerInAnyWatchList={!!isPlayerInAnyWatchlist}
          onAddPress={onAdd}
          player={player}
          promptRef={promptRef}
          showActionsheet={showActionsheet}
          isPlayerInAnyWatchlist={!!isPlayerInAnyWatchlist}
          isSmallScreen={isSmallScreen}
          onCloseActionSheet={onCloseActionSheet}
          onAddWatchlistPress={onAddWatchlistPress}
          isDark={isDark}
        />
      </AppleStyleSwipeableRow>
    </Reanimated.View>
  );
RecommendationPlayerView.displayName = "RecommendationPlayerView";
export default memo(RecommendationPlayerView);
